import React from 'react';
import { ShieldCheck, ShieldAlert, ShieldQuestion, Shield } from 'lucide-react';

/**
 * ConfidenceChip — Trust Infrastructure component.
 *
 * Displays a colour-coded pill indicating model confidence. Hover reveals the
 * one-line reason. Use on every numeric output of the Predict module.
 *
 * Props:
 *   level: 'high' | 'moderate' | 'low' | 'insufficient' (required)
 *   reason: short string explaining why this level was assigned (required)
 *   compact: boolean — smaller variant for inline use (default false)
 */
const LEVEL_CONFIG = {
  high: {
    label: 'High confidence',
    icon: ShieldCheck,
    bg: '#E6F4EA',
    fg: '#1B5E20',
    border: '#A5D6A7',
  },
  moderate: {
    label: 'Moderate confidence',
    icon: Shield,
    bg: '#FFF8E1',
    fg: '#7C5800',
    border: '#FFE082',
  },
  low: {
    label: 'Low confidence',
    icon: ShieldAlert,
    bg: '#FCE4EC',
    fg: '#8B1A4A',
    border: '#F8BBD0',
  },
  insufficient: {
    label: 'Insufficient data',
    icon: ShieldQuestion,
    bg: '#ECEFF1',
    fg: '#37474F',
    border: '#CFD8DC',
  },
};

export default function ConfidenceChip({ level, reason, compact = false }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.insufficient;
  const Icon = cfg.icon;
  return (
    <span
      title={reason || cfg.label}
      className="inline-flex items-center gap-1.5"
      style={{
        background: cfg.bg,
        color: cfg.fg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 999,
        padding: compact ? '3px 8px' : '4px 10px',
        fontSize: compact ? 11 : 12,
        fontWeight: 600,
        letterSpacing: '0.01em',
        cursor: 'help',
      }}
      data-testid={`confidence-chip-${level}`}
    >
      <Icon size={compact ? 11 : 13} strokeWidth={2.2} />
      {cfg.label}
    </span>
  );
}
